import { IPartnerInquiries } from '@llws/lift-entity-interfaces';
import { concat } from '@jamesgmarks/utilities';
import { Equal, IQueryCommandOptions, convertQueryCommandToQueryString } from '@llws/dynamic-router-utils';
import {
  IApiResponse,
  IMetaResponse,
  IModifyPayload,
} from '@/shared';
import { IAction } from '@/shared/interfaces/store-base';

import { adminRequest } from '@/shared/request-configs/admin-request';
import { StoreBaseImplementationV2 } from '../../shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/syndication-api/partner_inquiries';
const listViewName: string = '';
const showLogging: boolean = false;

interface IPartnerInquiriesState {
  list: IApiResponse<IPartnerInquiries[], IMetaResponse> | null;
  single: IApiResponse<IPartnerInquiries, IMetaResponse> | null;
  promotedListingContractInquiries: IApiResponse<IPartnerInquiries[], IMetaResponse> | null;
}

const defaultGetListFilters: IQueryCommandOptions = {};
const defaultGetSingleFilters: IQueryCommandOptions = {};

const getContractInquiriesFilters: IQueryCommandOptions = {
  alias: 'i',
  where: [
    Equal('i.inquiryType', 'contract'),
    Equal('i.status', 'requested'),
  ],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IPartnerInquiries>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'partner-inquiries/'

  state: {
    ...base.state,

    promotedListingContractInquiries: undefined as IApiResponse<IPartnerInquiries, IMetaResponse> | undefined,

  },

  getters: {
    ...base.getters,

    promotedListingContractInquiries: (state: IPartnerInquiriesState) => state.promotedListingContractInquiries?.data || [],

    promotedListingContractInquiryBaseSubIds: (state: IPartnerInquiriesState) => (state.promotedListingContractInquiries?.data
      && state.promotedListingContractInquiries?.data.length !== 0
      ? (state.promotedListingContractInquiries?.data!.map((contract: IPartnerInquiries) => contract.contractBaseSubscriptionId)) : []),
  },

  mutations: {
    ...base.mutations,

    setPromotedListingContractInquiries: (
      state: IPartnerInquiriesState,
      payload: IApiResponse<IPartnerInquiries[], IMetaResponse>,
    ) => {
      state.promotedListingContractInquiries = Array.isArray(payload)
        ? { ...state.promotedListingContractInquiries && state.promotedListingContractInquiries, ...{ data: payload } }
        : payload;
    },
    appendToList: (
      state: IPartnerInquiriesState,
      payload: IApiResponse<IPartnerInquiries[], IMetaResponse>,
    ) => {
      state.list = Array.isArray(payload)
        ? { ...state.list && state.list, ...{ data: concat(payload, state.list?.data!) } }
        : payload;
    },
  },

  actions: {
    ...base.actions,

    getPromotedListingContractInquiries: async (ctx: IAction<IPartnerInquiriesState>) => {
      const queryString = convertQueryCommandToQueryString(getContractInquiriesFilters);
      const result = await adminRequest.get(adminGatewayResourcePath, queryString);
      ctx.commit('setPromotedListingContractInquiries', result);
    },

    savePromotedListingContractInquiries: async (ctx: IAction<IPartnerInquiriesState>, payload: IModifyPayload<number, IPartnerInquiries[]>) => {
      const { promotedListingContractInquiries } = ctx.getters;
      const saved = await adminRequest.post(adminGatewayResourcePath, payload.data);
      const result = [
        ...saved.data,
        ...promotedListingContractInquiries,
      ];
      ctx.commit('setPromotedListingContractInquiries', result);
    },
  },
};
